import React, { useState, useRef } from 'react';
import Icon from '../Icon';
import { C } from '../../utils/styles';
import api from '../../utils/api';
import { useConfig } from '../../context/ConfigContext';
import { parseCensusFile, pickPrimarySheet } from '../../utils/censusFileParser';
import { detectFormat, FORMAT_LABELS } from '../../utils/censusFormatDetection';
import { transformSheet } from '../../utils/censusTransformers';
import { computeTaxValidation } from '../../utils/censusValidation';

import UploadModeCards from './smartUpload/UploadModeCards';
import FormatDetectionCard from './smartUpload/FormatDetectionCard';
import MissingDataPrompts from './smartUpload/MissingDataPrompts';
import CalculationValidationPanel from './smartUpload/CalculationValidationPanel';
import EarningsStabilityCard from './smartUpload/EarningsStabilityCard';
import ExistingDeductionsCard from './smartUpload/ExistingDeductionsCard';
import StyledSelect from '../StyledSelect';

import { CSV_TEMPLATE, CSV_TEMPLATE_FILENAME } from '../../data/csvTemplate';

// Smart Census Upload — multi-format detection, transformation, validation, and
// guided missing-data prompts. Drop-in replacement for the original drag-and-
// drop UI inside CensusUploadStep. On final accept, persists employees through
// `api.clients.uploadCensus(clientId, { employees })`, mirroring the legacy
// shape so the rest of the app keeps working.

const STAGES = {
  SELECT: 'select',
  PARSING: 'parsing',
  PICK_SHEET: 'pick_sheet',
  DETECTED: 'detected',
  MISSING: 'missing',
  REVIEW: 'review',
  UPLOADING: 'uploading',
  ERROR: 'error',
};

export default function SmartCensusUpload({ client, onUploaded, onCancel, persist = true, compact = false }) {
  const { config } = useConfig();
  const fileInputRef = useRef(null);

  const [stage, setStage] = useState(STAGES.SELECT);
  const [parsed, setParsed] = useState(null);          // { sheets, fileName, fileType, rawText }
  const [activeSheet, setActiveSheet] = useState(null);
  const [detection, setDetection] = useState(null);
  const [chosenFormat, setChosenFormat] = useState(null);
  const [transform, setTransform] = useState(null);    // { employees, meta }
  const [employees, setEmployees] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = CSV_TEMPLATE_FILENAME;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setStage(STAGES.PARSING);
    setErrorMsg('');
    try {
      const result = await parseCensusFile(file);
      setParsed(result);
      const sheetCount = result.sheets.length;
      if (sheetCount === 0) {
        setErrorMsg('We could not read any data out of that file.');
        setStage(STAGES.ERROR);
        return;
      }
      // Auto-pick a primary sheet if there is only one OR the best score is
      // overwhelmingly clear. Otherwise, ask the user.
      const usableSheets = result.sheets.filter((s) => s.meta.dataRowCount > 0 && s.headers.length > 0);
      if (usableSheets.length === 0) {
        setErrorMsg('No usable data sheet found in this workbook.');
        setStage(STAGES.ERROR);
        return;
      }
      if (usableSheets.length === 1) {
        applySheet(usableSheets[0]);
        return;
      }
      // Multi-sheet: always let the user choose. We pre-detect each sheet so
      // the picker can show the auto-detected format next to the sheet name,
      // but we never silently auto-pick because (a) workbooks like TriMed
      // contain both a partner-template tab AND a payroll-export tab and
      // each has a different downstream meaning; (b) showing the choice is
      // a confidence-builder more than a friction point.
      setActiveSheet(usableSheets[0]);
      setStage(STAGES.PICK_SHEET);
    } catch (err) {
      console.error('parseCensusFile failed:', err);
      setErrorMsg(err?.message || 'Failed to read the uploaded file.');
      setStage(STAGES.ERROR);
    }
  };

  const applySheet = (sheet, preDetection = null) => {
    const det = preDetection || detectFormat(sheet);
    setActiveSheet(sheet);
    setDetection(det);
    setStage(STAGES.DETECTED);
  };

  const handleConfirmFormat = (formatKey) => {
    setChosenFormat(formatKey);
    const result = transformSheet(activeSheet, formatKey);
    setTransform(result);
    setEmployees(result.employees);
    // Skip MISSING stage if everything is filled in already
    const m = result.meta || {};
    const hasMissing = (m.missingFields || []).some((f) => f === 'filing' || f === 'state');
    setStage(hasMissing ? STAGES.MISSING : STAGES.REVIEW);
  };

  const handleMissingComplete = (updatedEmployees) => {
    setEmployees(updatedEmployees);
    setStage(STAGES.REVIEW);
  };

  const handleAccept = async () => {
    setStage(STAGES.UPLOADING);
    try {
      // Persist normalized employees. Backend stores as-is; we include both
      // `salary` (engine canonical) and `annualSalary` (legacy alias) so that
      // any older code paths that read `annualSalary` keep working.
      const payload = employees.map((e) => ({
        ...e,
        salary: Number(e.salary || e.annualSalary || 0),
        annualSalary: Number(e.salary || e.annualSalary || 0),
        filing: e.filing || null,
        filingStatus: e.filingStatus || e.filing || null,
      }));
      // Persist to backend only when (a) we have a client.id AND (b) the
      // host hasn't opted out of automatic persistence. This lets BulkView /
      // ad-hoc analysis flows reuse the smart upload UI without forcing a
      // round-trip when there is no client to attach the census to.
      if (client?.id && persist !== false) {
        await api.clients.uploadCensus(client.id, { employees: payload });
      }
      if (onUploaded) {
        onUploaded({ employees: payload, meta: transform?.meta || {} });
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('uploadCensus failed:', err);
      setErrorMsg(err?.message || 'Failed to upload census. Please try again.');
      setStage(STAGES.ERROR);
    }
  };

  // ---------- render ----------

  const validation = transform?.meta?.hasEmployerTaxColumns
    ? computeTaxValidation(employees, config)
    : null;

  return (
    <div data-testid="smart-census-upload">
      {/* Stage: SELECT */}
      {stage === STAGES.SELECT && (
        <SelectStage
          fileInputRef={fileInputRef}
          onPickStandard={() => fileInputRef.current?.click()}
          onPickAuto={() => fileInputRef.current?.click()}
          onDownloadTemplate={handleDownloadTemplate}
          onFileSelect={handleFile}
        />
      )}

      {stage === STAGES.PARSING && (
        <CenteredCard icon="hourglass-outline" title="Reading your file..." subtitle="Detecting headers and counting rows" />
      )}

      {stage === STAGES.PICK_SHEET && parsed && (
        <SheetPicker
          sheets={parsed.sheets}
          onPick={(s) => applySheet(s)}
          onBack={() => setStage(STAGES.SELECT)}
        />
      )}

      {stage === STAGES.DETECTED && activeSheet && detection && (
        <FormatDetectionCard
          detection={detection}
          sheet={activeSheet}
          fileName={parsed?.fileName}
          onConfirm={handleConfirmFormat}
          onOverride={handleConfirmFormat}
          onBack={() => setStage(STAGES.SELECT)}
        />
      )}

      {stage === STAGES.MISSING && transform && (
        <MissingDataPrompts
          employees={employees}
          meta={transform.meta}
          clientId={client?.id}
          onComplete={handleMissingComplete}
        />
      )}

      {stage === STAGES.REVIEW && transform && (
        <ReviewStage
          employees={employees}
          meta={transform.meta}
          validation={validation}
          chosenFormat={chosenFormat}
          onAccept={handleAccept}
          onBack={() => setStage(STAGES.DETECTED)}
        />
      )}

      {stage === STAGES.UPLOADING && (
        <CenteredCard icon="cloud-upload-outline" title="Uploading census..." subtitle="Persisting employees to your client record" />
      )}

      {stage === STAGES.ERROR && (
        <ErrorCard
          message={errorMsg}
          onRetry={() => setStage(STAGES.SELECT)}
          onCancel={onCancel}
        />
      )}

      {onCancel && stage !== STAGES.UPLOADING && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button
            onClick={onCancel}
            data-testid="smart-census-upload-cancel"
            style={{
              padding: '8px 14px', background: 'transparent', color: C.textMid,
              border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancel upload
          </button>
        </div>
      )}
    </div>
  );
}

// ----- sub-stages -----

function SelectStage({ fileInputRef, onPickStandard, onPickAuto, onDownloadTemplate, onFileSelect }) {
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  };

  return (
    <div data-testid="smart-upload-select">
      <UploadModeCards
        onPickStandard={onPickStandard}
        onPickAuto={onPickAuto}
        onDownloadTemplate={onDownloadTemplate}
      />

      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        style={{
          padding: 36,
          border: `2px dashed ${C.border}`,
          borderRadius: 12,
          textAlign: 'center',
          cursor: 'pointer',
          background: C.bg,
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = C.blue;
          e.currentTarget.style.background = C.blueLight;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.background = C.bg;
        }}
      >
        <Icon name="cloud-upload-outline" style={{ fontSize: 40, color: C.blue, marginBottom: 12 }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          Drag and drop your file here
        </div>
        <div style={{ fontSize: 13, color: C.textMid, marginBottom: 12 }}>
          or click to browse — supports .csv, .xlsx, .xls
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={(e) => onFileSelect(e.target.files[0])}
        style={{ display: 'none' }}
      />
    </div>
  );
}

function SheetPicker({ sheets, onPick, onBack }) {
  const usable = sheets.filter((s) => s.meta.dataRowCount > 0 && s.headers.length > 0);
  return (
    <div data-testid="smart-upload-sheet-picker" style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Multiple sheets found</div>
      <div style={{ fontSize: 13, color: C.textMid, marginBottom: 16 }}>
        Pick the sheet that contains your employee census.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {usable.map((s) => {
          const det = detectFormat(s);
          return (
            <button
              key={s.name}
              onClick={() => onPick(s)}
              data-testid={`sheet-pick-${s.name.replace(/\s+/g, '-')}`}
              style={{
                padding: 14, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
                textAlign: 'left', cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.blue; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
                    {s.meta.dataRowCount} rows · {s.headers.length} columns · headers on row {s.headerRowIndex + 1}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: C.textMid, textTransform: 'uppercase', fontWeight: 700 }}>
                  Detected: {FORMAT_LABELS[det.format]}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}>
        <button onClick={onBack} style={{
          padding: '8px 14px', background: 'transparent', color: C.textMid,
          border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>
          Use a different file
        </button>
      </div>
    </div>
  );
}

function ReviewStage({ employees, meta, validation, chosenFormat, onAccept, onBack }) {
  const inferredCount = employees.filter((e) => e.filingStatusInferred).length;
  const totalSalary = employees.reduce((s, e) => s + Number(e.salary || e.annualSalary || 0), 0);
  const avgSalary = employees.length > 0 ? Math.round(totalSalary / employees.length) : 0;

  return (
    <div data-testid="smart-upload-review">
      <div style={{
        background: '#fff', border: `1px solid ${C.green}`, borderRadius: 12, padding: 24, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, background: C.greenLight, color: C.green,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon name="checkmark-circle" style={{ fontSize: 22 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              Ready to accept — {employees.length} employees parsed from {FORMAT_LABELS[chosenFormat] || chosenFormat}
            </div>
            <div style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>
              Average annual salary: <strong>${avgSalary.toLocaleString()}</strong>
              {inferredCount > 0 && (
                <> · <strong>{inferredCount}</strong> employees have inferred filing status (flagged for later replacement)</>
              )}
            </div>
          </div>
        </div>
      </div>

      <CalculationValidationPanel validation={validation} />
      <EarningsStabilityCard employees={employees} hide={!meta?.hasEarningsBreakdown} />
      <ExistingDeductionsCard existingDeductions={meta?.existingDeductions} />

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px 20px', background: 'transparent', color: C.textMid,
            border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Back
        </button>
        <button
          data-testid="smart-upload-accept"
          onClick={onAccept}
          style={{
            padding: '12px 24px', background: C.green, color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Icon name="checkmark-circle" style={{ fontSize: 18 }} />
          Accept & save {employees.length} employees
        </button>
      </div>
    </div>
  );
}

function CenteredCard({ icon, title, subtitle }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 36, textAlign: 'center',
    }}>
      <Icon name={icon} style={{ fontSize: 36, color: C.blue, marginBottom: 12 }} />
      <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, color: C.textMid, marginTop: 6 }}>{subtitle}</div>}
    </div>
  );
}

function ErrorCard({ message, onRetry, onCancel }) {
  return (
    <div data-testid="smart-upload-error" style={{
      background: '#fff', border: `1px solid ${C.red || '#dc2626'}`, borderRadius: 12, padding: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 12, marginBottom: 12 }}>
        <Icon name="alert-circle" style={{ fontSize: 22, color: C.red || '#dc2626' }} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>We couldn't parse that file</div>
          <div style={{ fontSize: 13, color: C.textMid, marginTop: 4 }}>{message}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={onRetry} style={{
          padding: '10px 20px', background: C.blue, color: '#fff', border: 'none', borderRadius: 8,
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
          Try a different file
        </button>
        {onCancel && (
          <button onClick={onCancel} style={{
            padding: '10px 20px', background: 'transparent', color: C.textMid,
            border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
